import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Plus, Edit, Trash2, Tag, Percent, DollarSign, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DiscountCodesPage = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, code: null });
  const [form, setForm] = useState({
    code: '',
    discount_type: 'percent',
    discount_value: '',
    min_order_amount: '',
    max_uses: '',
    once_per_gallery: false,
    expires_at: '',
    active: true
  });

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      const response = await axios.get(`${API}/discount-codes`);
      setCodes(response.data);
    } catch (error) {
      toast.error('Failed to load discount codes');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      code: '',
      discount_type: 'percent',
      discount_value: '',
      min_order_amount: '',
      max_uses: '',
      once_per_gallery: false,
      expires_at: '',
      active: true
    });
    setEditingCode(null);
  };

  const openEditDialog = (code) => {
    setEditingCode(code);
    setForm({
      code: code.code,
      discount_type: code.discount_type,
      discount_value: code.discount_value.toString(),
      min_order_amount: code.min_order_amount?.toString() || '',
      max_uses: code.max_uses?.toString() || '',
      once_per_gallery: code.once_per_gallery || false,
      expires_at: code.expires_at?.split('T')[0] || '',
      active: code.active
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.code || !form.discount_value) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const data = {
        code: form.code.toUpperCase(),
        discount_type: form.discount_type,
        discount_value: parseFloat(form.discount_value),
        min_order_amount: form.min_order_amount ? parseFloat(form.min_order_amount) : null,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        once_per_gallery: form.once_per_gallery,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        active: form.active
      };

      if (editingCode) {
        await axios.put(`${API}/discount-codes/${editingCode.id}`, data);
        toast.success('Discount code updated');
      } else {
        await axios.post(`${API}/discount-codes`, data);
        toast.success('Discount code created');
      }

      setShowDialog(false);
      resetForm();
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save discount code');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.code) return;
    try {
      await axios.delete(`${API}/discount-codes/${deleteDialog.code.id}`);
      toast.success('Discount code deleted');
      setDeleteDialog({ open: false, code: null });
      fetchCodes();
    } catch (error) {
      toast.error('Failed to delete discount code');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Discount Codes</h1>
          <p className="text-slate-600 mt-1">Create promotional codes and discounts</p>
        </div>
        <Button 
          className="bg-blue-600 hover:bg-blue-700" 
          onClick={() => { resetForm(); setShowDialog(true); }}
          data-testid="new-discount-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Discount Code
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-48 bg-slate-200 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : codes.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {codes.map((code) => (
            <Card key={code.id} className={`border-slate-200 ${!code.active || isExpired(code.expires_at) ? 'opacity-60' : ''}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="font-mono text-xl tracking-wider">{code.code}</CardTitle>
                    <div className="flex gap-2 mt-2">
                      {code.discount_type === 'percent' ? (
                        <Badge className="bg-blue-100 text-blue-700">
                          <Percent className="h-3 w-3 mr-1" />
                          {code.discount_value}% OFF
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700">
                          <DollarSign className="h-3 w-3 mr-1" />
                          {formatCurrency(code.discount_value)} OFF
                        </Badge>
                      )}
                      {!code.active && <Badge variant="outline" className="text-slate-500">Inactive</Badge>}
                      {isExpired(code.expires_at) && <Badge variant="outline" className="text-red-500">Expired</Badge>}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm text-slate-600 mb-4">
                  {code.min_order_amount && (
                    <p>Min. order: {formatCurrency(code.min_order_amount)}</p>
                  )}
                  {code.max_uses && (
                    <p>Uses: {code.times_used || 0} / {code.max_uses}</p>
                  )}
                  {code.once_per_gallery && (
                    <p className="text-blue-600 font-medium">One use per gallery</p>
                  )}
                  {code.expires_at && (
                    <p className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Expires: {new Date(code.expires_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => openEditDialog(code)}
                    data-testid={`edit-code-${code.id}`}
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setDeleteDialog({ open: true, code })}
                    data-testid={`delete-code-${code.id}`}
                  >
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <Tag className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">No discount codes yet</h3>
            <p className="text-slate-500 mb-6">Create promotional codes to offer discounts</p>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => { resetForm(); setShowDialog(true); }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Discount Code
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Discount Code Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCode ? 'Edit Discount Code' : 'New Discount Code'}</DialogTitle>
            <DialogDescription>
              {editingCode ? 'Update discount code details' : 'Create a new promotional code'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">Code *</Label>
              <Input
                id="code"
                placeholder="e.g., SUMMER20"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                className="font-mono uppercase"
                data-testid="discount-code-input"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Discount Type</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger data-testid="discount-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentage</SelectItem>
                    <SelectItem value="fixed">Fixed Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="value">
                  {form.discount_type === 'percent' ? 'Percentage' : 'Amount (CAD)'} *
                </Label>
                <Input
                  id="value"
                  type="number"
                  step={form.discount_type === 'percent' ? '1' : '0.01'}
                  min="0"
                  max={form.discount_type === 'percent' ? '100' : undefined}
                  placeholder={form.discount_type === 'percent' ? '20' : '10.00'}
                  value={form.discount_value}
                  onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                  data-testid="discount-value-input"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="min_order">Min. Order (CAD)</Label>
                <Input
                  id="min_order"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Optional"
                  value={form.min_order_amount}
                  onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })}
                  data-testid="min-order-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_uses">Max Uses</Label>
                <Input
                  id="max_uses"
                  type="number"
                  min="1"
                  placeholder="Unlimited"
                  value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  data-testid="max-uses-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expires">Expiration Date</Label>
              <Input
                id="expires"
                type="date"
                value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                data-testid="expires-input"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div>
                <Label htmlFor="once_per_gallery">One Use Per Gallery</Label>
                <p className="text-xs text-slate-500">Code can only be used once per gallery</p>
              </div>
              <Switch
                id="once_per_gallery"
                checked={form.once_per_gallery}
                onCheckedChange={(checked) => setForm({ ...form, once_per_gallery: checked })}
                data-testid="once-per-gallery-switch"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label htmlFor="active">Active</Label>
                <p className="text-xs text-slate-500">Code can be used at checkout</p>
              </div>
              <Switch
                id="active"
                checked={form.active}
                onCheckedChange={(checked) => setForm({ ...form, active: checked })}
                data-testid="active-switch"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-discount-btn">
                {editingCode ? 'Update' : 'Create'} Code
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, code: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Discount Code</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteDialog.code?.code}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, code: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiscountCodesPage;
